import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef } from 'react';
import ReactDom from 'react-dom';

import styles from './index.module.scss';

interface Props {
  visible: boolean;
  arrowSize?: number;
  arrowToTarget?: number;
  tip: ReactNode | string;
  tipClassName?: string;
  arrowClassName?: string;
  tipStyles?: React.CSSProperties;
  arrowStyles?: React.CSSProperties;
  targetEl: React.MutableRefObject<HTMLDivElement>;
  tipWidth: number;
}

type TipStyle = {
  top?: string;
  left?: string;
  bottom?: string;
  right?: string;
  transform?: string;
};

type ArrowStyle = {
  borderLeft?: string;
  borderRight?: string;
  borderTop?: string;
  borderBottom?: string;
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
  transform?: string;
};

export default function AlcoholTooltipCommon(props: Props) {
  const {
    visible,
    tip,
    arrowSize = 8,
    arrowToTarget = 4,
    tipStyles,
    arrowStyles,
    tipClassName,
    arrowClassName,
    targetEl,
    tipWidth
  } = props;

  const idRef = useRef('');

  useEffect(() => {
    if (visible && targetEl.current) {
      const size = targetEl.current.getBoundingClientRect();

      const left = size.left + document.documentElement.scrollLeft;
      const top = size.top + document.documentElement.scrollTop;
      const { tip, arrow } = createStyles(size);
      const node = document.createElement('div');
      idRef.current = `toolTip: + ${new Date().getTime()}`;
      node.setAttribute('id', idRef.current);
      node.style.position = 'absolute';
      node.style.left = `${left}px`;
      node.style.top = `${top}px`;
      const view = renderHtml(tip, arrow);
      ReactDom.render(view, node);
      document.body.appendChild(node);
    } else {
      destroyEl();
    }
  }, [visible]);

  useEffect(() => {
    window.addEventListener('scroll', destroyEl);
    return () => {
      window.removeEventListener('scroll', destroyEl);
      destroyEl();
    };
  }, []);

  const createStyles = size => {
    const { width: targetWidth, height: targetHeight, left: targetLeft, right: targetRight } = size;
    const screenWidth = document.documentElement.clientWidth;
    const tipTop: TipStyle = {
      bottom: `${arrowToTarget + arrowSize}px`,
      left: '50%',
      transform: 'translateX(-50%)'
    };
    const topTipArrow: ArrowStyle = {
      borderTop: `${arrowSize}px solid rgba(102, 102, 102)`,
      borderRight: `${arrowSize}px solid transparent`,
      borderLeft: `${arrowSize}px solid transparent`,
      bottom: `${arrowToTarget}px`,
      left: `${targetWidth / 2 - arrowSize}px`
    };

    if (targetLeft < screenWidth / 2) {
      //在左边
      if (tipWidth / 2 >= targetLeft) {
        delete tipTop.transform;
        tipTop.left = `-${targetWidth}px`;
      }
    } else if (targetLeft > screenWidth / 2) {
      //在右边
      if (tipWidth / 2 >= screenWidth - targetLeft) {
        delete tipTop.left;
        delete tipTop.transform;
        tipTop.right = `-${targetWidth}px`;
      }
    }
    return {
      tip: tipTop,
      arrow: topTipArrow
    };
  };

  const destroyEl = () => {
    const el = document.getElementById(idRef.current);
    if (el) {
      document.body.removeChild(el);
      idRef.current = '';
    }
  };

  const renderHtml = (tipStyle: TipStyle, arrowStyle: ArrowStyle) => {
    return (
      <>
        <div
          data-role="tooltip"
          style={{ ...tipStyle, ...tipStyles }}
          className={classNames(styles.tooltip, tipClassName)}>
          {tip}
        </div>
        <div
          data-role="toolarrow"
          style={{ ...arrowStyle, ...arrowStyles }}
          className={classNames(styles.arrow, arrowClassName)}></div>
      </>
    );
  };

  return <></>;
}
